import { isValidSearchString } from '@/services/helpers';
import { SEARCH_DEBOUNCE_MS } from '@/services/userLocation';

const debounceTimers = {};

function runDebounced(key, fn, delay = SEARCH_DEBOUNCE_MS) {
  return new Promise((resolve, reject) => {
    if (debounceTimers[key]) {
      clearTimeout(debounceTimers[key]);
    }
    debounceTimers[key] = setTimeout(async () => {
      delete debounceTimers[key];
      try {
        resolve(await fn());
      } catch (error) {
        reject(error);
      }
    }, delay);
  });
}

/**
 * Forward address search via backend (HERE Autosuggest proxy).
 */
export async function searchAddresses(http, query, userId, at) {
  if (!isValidSearchString(query)) {
    return [];
  }
  const payload = {
    string: query,
    user_id: userId ?? null,
  };
  if (at?.lat != null && at?.lng != null) {
    payload.at = { lat: at.lat, lng: at.lng };
  }
  const response = await http.post(`${http.apiUrl()}/subscriptions/search-points`, payload);
  return mapSearchResults(response?.data);
}

export function searchAddressesDebounced(http, query, userId, debounceKey, at) {
  if (!isValidSearchString(query)) {
    return Promise.resolve([]);
  }
  return runDebounced(debounceKey, () => searchAddresses(http, query, userId, at));
}

/**
 * Normalize backend items to planner/modal shape.
 */
export function mapSearchResults(data) {
  if (!Array.isArray(data) || !data.length) {
    return [];
  }
  return data
    .filter((item) => item?.position?.lat != null && item?.position?.lng != null)
    .map((item) => ({
      position: {
        Latitude: item.position.lat,
        Longitude: item.position.lng,
        lat: item.position.lat,
        lng: item.position.lng,
      },
      address: item.address?.label || item.title || '',
    }));
}

/**
 * Modal-friendly shape: { position: { lat, lng }, address }.
 */
export function mapSearchResultsForModal(data) {
  if (!Array.isArray(data) || !data.length) {
    return [];
  }
  return data
    .filter((item) => item?.position?.lat != null && item?.position?.lng != null)
    .map((item) => ({
      position: { lat: item.position.lat, lng: item.position.lng },
      address: item.address?.label || item.title || '',
    }));
}

export async function searchAddressesForModal(http, query, userId, at) {
  if (!query?.trim()) {
    return [];
  }
  const payload = {
    string: query,
    user_id: userId ?? null,
  };
  if (at?.lat != null && at?.lng != null) {
    payload.at = { lat: at.lat, lng: at.lng };
  }
  const response = await http.post(`${http.apiUrl()}/subscriptions/search-points`, payload).catch(() => null);
  return mapSearchResultsForModal(response?.data);
}

export function searchAddressesForModalDebounced(http, query, userId, debounceKey, at) {
  if (!query?.trim()) {
    return Promise.resolve([]);
  }
  return runDebounced(debounceKey, () => searchAddressesForModal(http, query, userId, at));
}
